/**
 * HealthDisplay component from core content pack.
 *
 * Displays the character's health bar.
 */

import { Heart } from 'lucide-react';
import { observer } from 'mobx-react-lite';

import type { CharacterStat } from '@idle-depths/shared';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind classes.
 * Duplicated here to avoid dependency on @/lib/utils in content packs.
 */
function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Props for HealthDisplay component.
 */
interface HealthDisplayProperties {
  /** Character's stats record */
  stats?: Record<string, CharacterStat>;
  /** Additional class name */
  className?: string;
}

/**
 * HealthDisplay shows the character's health as a progress bar.
 *
 * Injected into the 'character-stats' UI slot by the core pack.
 */
export const HealthDisplay = observer(function HealthDisplay({
  stats,
  className,
}: HealthDisplayProperties): React.ReactElement | null {
  const healthStat = stats?.['health'];

  if (!healthStat) {
    return null;
  }

  const { currentValue, maximumValue } = healthStat;
  const percentage =
    maximumValue && maximumValue > 0
      ? Math.min(100, Math.max(0, (currentValue / maximumValue) * 100))
      : 100;

  // Color based on health percentage
  const getHealthColor = (): string => {
    if (percentage > 60) {
      return 'bg-game-health';
    }
    if (percentage > 30) {
      return 'bg-treasure-amber';
    }
    return 'bg-destructive';
  };

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Heart className="h-4 w-4 text-game-health" />
          <span className="font-medium">Health</span>
        </div>
        <span className="text-foreground tabular-nums">
          {currentValue}/{maximumValue ?? '∞'}
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <div
          className={cn('h-full rounded-full transition-all duration-300', getHealthColor())}
          style={{ width: `${String(percentage)}%` }}
        />
      </div>
    </div>
  );
});

export default HealthDisplay;
